//NPM LIBRARIES
import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

//REDUX ACTIONS AND SELECTORS
import { getVerificationViewData, GET_VERIFICATION_VIEW_DATA_SUCCESS } from '../../FormStore/Actions/FormServiceActions/DigitalLicenceServiceActions/VerificationViewDataAction';
import { addPageAlert } from '../../PortalStore/Actions/PageAlertActions';
import { getFormServiceStatusSelector } from '../../FormStore/Selectors/FormDataSelectors';
import { getFormPropertiesSelector } from '../../FormStore/Selectors/FormDataSelectors';

//Components
import Heading from '../../components/CASA/Heading';
import SortedTable from '../../components/Portal/SortedTable';
import TableDataProperties from './VerificationTableDataProperties.json';
import CASASpinner from '../../components/Portal/CASASpinner';

class VerificationPage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            modal: false,
            licenceType: null,
            token: null,
            formLoaded: false,
            licenceCancelled: true
        };
    }

    componentDidMount = () => {
        if (this.props.match.params.licenceType && this.props.match.params.token) {
            this.setState({
                licenceType: this.props.match.params.licenceType,
                token: this.props.match.params.token
            });

            this.loadLicenceData(this.props.match.params.token, this.props.match.params.licenceType);
        } else {
            this.setState({ formLoaded: true });
            this.props.dispatch(addPageAlert({
                level: 'error',
                title: 'Authorisation details not confirmed',
                message: 'The authorisation scanned <b>has not been found or is not an official authorisation</b> issued by the Civil Aviation Safety Authority (CASA).'
            }));
        }
    };

    loadLicenceData(token, licenceType) {
        const webappurl = this.props.B2CSettings.items.webappurlPortalApi;

        const accessToken = this.props.B2CSettings.items.accesstokenPortal;

        this.props.dispatch(getVerificationViewData(webappurl, accessToken, token, licenceType))
            .then(response => {
                if (response.type === GET_VERIFICATION_VIEW_DATA_SUCCESS) {
                    this.setState({ formLoaded: true });
                    // Check if licence is cancelled or suspended
                    if (licenceType === "FCL" && (!response.payload?.categoryRatings?.components ||
                        response.payload.categoryRatings.components.length <= 0)
                        || licenceType === "RePL" && (!response.payload?.rpaRatings?.rpaCategory ||
                            response.payload.rpaRatings.rpaCategory.length <= 0)
                        || licenceType === "AROC" && (response.payload?.hasSuspended || !response.payload?.arocDetails)
                    ) {
                        this.props.dispatch(addPageAlert({
                            level: 'error',
                            title: 'Authorisation details not confirmed',
                            message: 'The authorisation scanned <b>has not been found or is not an official authorisation</b> issued by the Civil Aviation Safety Authority (CASA).'
                        }));

                    } else {
                        this.setState({ licenceCancelled: false });
                        if (licenceType === "FCL" || licenceType === "RePL") {
                            this.props.dispatch(addPageAlert({
                                level: 'success',
                                title: '',
                                message: '<h3>Licence confirmed</h3><p>The licence scanned is an official digital licence issued by the Civil Aviation Safety Authority (CASA). The details displayed within the licence are true and correct as per CASA&apos;s official records.</p><p>Further information on CASA issued licences can be found on <a href="https://www.casa.gov.au/licences-and-certificates/pilots/pilot-licences">CASA&apos;s website</a></p>'
                            }));
                        }
                        else if (licenceType === "AROC") {
                            this.props.dispatch(addPageAlert({
                                level: 'success',
                                title: '',
                                message: '<h3>Certificate confirmed</h3><p>The certificate scanned is an official digital certificate issued by the Civil Aviation Safety Authority (CASA). The details displayed within the certificate are true and correct as per CASA&apos;s official records.</p><p>Further information on CASA issued certificates can be found on <a href="https://www.casa.gov.au/licences-and-certificates/pilots/pilot-licences">CASA&apos;s website</a></p>'
                            }));
                        }
                        else {
                            this.props.dispatch(addPageAlert({
                                level: 'success',
                                title: '',
                                message: '<h3>Accreditation confirmed</h3><p>The accreditation scanned is an official digital accreditation issued by the Civil Aviation Safety Authority (CASA). The details displayed within the accreditation are true and correct as per CASA&apos;s official records.</p><p>Further information on CASA issued accreditations can be found on <a href="https://www.casa.gov.au/licences-and-certificates/pilots/pilot-licences">CASA&apos;s website</a></p>'
                            }));

                        }
                    }
                } else {
                    this.setState({ formLoaded: true });
                    this.props.dispatch(addPageAlert({
                        level: 'error',
                        title: 'Something has gone wrong',
                        message: 'Failed to confirm authorisation. Please contact us for further information.'
                    }));
                }
            })
            .catch(() => {
                this.setState({ formLoaded: true });
                this.props.dispatch(addPageAlert({
                    level: 'error',
                    title: 'Something has gone wrong',
                    message: 'Failed to confirm authorisation. Please contact us for further information.'
                }));
            });
    }

    getTableProperties(propertyType) {
        return TableDataProperties[propertyType];
    }

    generateTableComponent = (tableData, tableProperties, key) => {
        return (            
                <SortedTable
                    caption={tableProperties.title}
                    key={key}
                    data={tableData}
                    cols={tableProperties.properties}
                    sortby={tableProperties.sortBy} />
            
        );
    }

    generateTable = (reduxPropety, key) => {
        if (this.props.verificationViewData?.data[reduxPropety] &&
            this.props.verificationViewData.data[reduxPropety][key] &&
            this.props.verificationViewData.data[reduxPropety][key].length > 0) {

            let updatedTableData = {
                ...this.props.verificationViewData.data[reduxPropety][key]
            };

            return (
                this.generateTableComponent(
                    this.convertDateFormat(updatedTableData, key),
                    TableDataProperties[key],
                    key
                )
            );
        }
    }

    generateArocCertificateDetailsTable = (reduxPropety) => {
        if (this.props.verificationViewData?.data[reduxPropety]) {

            const arocDetails = this.props.verificationViewData.data[reduxPropety];
            let updatedTableData = [{ 'certificateType': 'Aeronautical Radio Operator Certificate', 'issueDate': arocDetails.acquisitionDate }];

            return (
                this.generateTableComponent(
                    this.convertDateFormat(updatedTableData, 'arocCertificateDetails'),
                    TableDataProperties['arocCertificateDetails'],
                    'arocCertificateDetails'
                )
            );
        }
    }

    generateArocElpDetailsTable = (reduxPropety) => {
        if (this.props.verificationViewData?.data[reduxPropety]) {

            const arocDetails = this.props.verificationViewData.data[reduxPropety];
            let updatedTableData = [];
            if(arocDetails.elpRemark) {
                updatedTableData.push({ 'proficiencyType': arocDetails.elpRemark });

                return (
                    this.generateTableComponent(
                        updatedTableData,
                        TableDataProperties['arocElpRemark'],
                        'arocElpRemark'
                    )
                );
            }
            else if (arocDetails.proficiencies && arocDetails.proficiencies.length > 0) {
                for (let i = 0; i < arocDetails.proficiencies.length; i++) {
                    let item = {
                        'proficiencyType': `AELP level ${arocDetails.proficiencies[i].result}`,
                        'validTo': arocDetails.proficiencies[i].validUntil ? moment(arocDetails.proficiencies[i].validUntil, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'N/A'
                    };
                    updatedTableData.push(item);
                }

                return (
                    this.generateTableComponent(
                        updatedTableData,
                        TableDataProperties['arocElpAelp'],
                        'arocElpAelp'
                    )
                );
            }
        }
    }

    convertDateFormat = (tableData, key) => {
        let updatedTableData = Object.values(tableData);

        TableDataProperties[key].properties.forEach(element => {
            if (element.type == 'date') {
                const updatedDataArray = updatedTableData.map(dataElement => {
                    let updatedDataElement = {
                        ...dataElement
                    };
                    if (dataElement[element.property]) {
                        updatedDataElement[element.property] = moment(dataElement[element.property]).format('DD/MM/YYYY');
                    }

                    return updatedDataElement;
                });

                updatedTableData = updatedDataArray;
            }
        });

        return updatedTableData;
    }

    generateTheoryExaminationTable = (reduxPropety, key) => {
        if (this.props.verificationViewData?.data[reduxPropety] &&
            this.props.verificationViewData.data[reduxPropety][key].length > 0) {
            let transformedStructure = this.props.verificationViewData.data[reduxPropety][key].map(element => {
                let resultObject = element.result;
                return {
                    ...element,
                    ...resultObject
                };
            });
            return (
                this.generateTableComponent(
                    this.convertDateFormat(transformedStructure, key),
                    TableDataProperties[key],
                    key
                )
            );
        }
    }

    generateIndividualTable = (key) => {
        if (!this.props.verificationViewData?.data?.licenceHolder) {
            return;
        }

        const individualData = {
            ...this.props.verificationViewData.data.licenceHolder[key]
        };

        if (!individualData) {
            return;
        }

        //Create table header for individual
        const tableHeader = {
            title: (this.state.licenceType === "FCL" || this.state.licenceType === "RePL") ? 'Licence holder details' : this.state.licenceType === "AROC" ? 'Certificate holder details' : 'Accreditation holder details',
            properties: [
                {
                    label: 'Name',
                    property: 'name',
                    visible: true,
                    type: 'string',
                    width: '50%'
                },
                {
                    label: individualData.names[0].firstName + ' ' + individualData.names[0].surname,
                    property: 'value',
                    visible: true,
                    type: 'string',
                    width: '50%'
                }
            ]
        };

        const tableBody = [
            {
                name: 'ARN and certificate number',
                value: individualData.aviationReferenceNumber
            },
            {
                name: 'Date of birth',
                value: moment(individualData.dateOfBirth, 'DD-MMM-YYYY').format('DD/MM/YYYY')
            },
            {
                name: 'Nationality',
                value: individualData.nationality
            }
        ];

        return this.generateTableComponent(tableBody, tableHeader, key);
    }

    generateTitle = (title, reduxPropety, keys) => {
        let showTitle = false;

        keys.forEach(key => {
            if (this.props.verificationViewData?.data[reduxPropety] &&
                this.props.verificationViewData?.data[reduxPropety][key] &&
                !this.props.verificationViewData?.data[reduxPropety][key].length <= 0) {
                showTitle = true;
            }
        });

        if (showTitle) {
            return <h4>{title}</h4>;
        }

        return;
    }

    renderTables = () => {
        if (this.state.licenceType === 'FCL') {
            return (
                <>
                    {/* Component tables */}
                    {this.generateTitle(
                        'Licence and aircraft category rating',
                        'categoryRatings',
                        ['components']
                    )}
                    {this.generateTable('categoryRatings', 'components')}
                    {/* Aircraft rating tables */}
                    {this.generateTitle(
                        'Aircraft ratings and endorsements',
                        'aircraftRatings',
                        ['classRating', 'typeRating', 'designRating']
                    )}
                    {this.generateTable('aircraftRatings', 'classRating')}
                    {this.generateTable('aircraftRatings', 'typeRating')}
                    {this.generateTable('aircraftRatings', 'designRating')}
                    {/* Operational rating tables */}
                    {this.generateTitle(
                        'Operational ratings and endorsements',
                        'operationalRatings',
                        [
                            'lowLevel', 'aerialApp', 'instrument', 'nightVFR',
                            'nightVision', 'flightInstructor', 'simulatorInstructor', 'privateInstrument',
                            'flightExaminer', 'flightEngineer', 'flightActivity'
                        ]
                    )}
                    {this.generateTable('operationalRatings', 'lowLevel')}
                    {this.generateTable('operationalRatings', 'aerialApp')}
                    {this.generateTable('operationalRatings', 'instrument')}
                    {this.generateTable('operationalRatings', 'nightVFR')}
                    {this.generateTable('operationalRatings', 'nightVision')}
                    {this.generateTable('operationalRatings', 'flightInstructor')}
                    {this.generateTable('operationalRatings', 'simulatorInstructor')}
                    {this.generateTable('operationalRatings', 'privateInstrument')}
                    {this.generateTable('operationalRatings', 'flightExaminer')}
                    {this.generateTable('operationalRatings', 'flightEngineer')}
                    {this.generateTable('operationalRatings', 'flightActivity')}
                    {/* Flight review proficiency tables */}
                    {this.generateTitle(
                        'Flight review and proficiency check - Aircraft ratings and operational ratings',
                        'proficiencyChecks',
                        ['categoryFlightReviews', 'operationalFlightReviews', 'proficiencyChecks']
                    )}
                    {this.generateTable('proficiencyChecks', 'categoryFlightReviews')}
                    {this.generateTable('proficiencyChecks', 'operationalFlightReviews')}
                    {this.generateTable('proficiencyChecks', 'proficiencyChecks')}
                    {/* Theory examinations tables */}
                    {this.generateTitle(
                        'English language proficiency',
                        'theoryExaminations',
                        ['theoryExaminations']
                    )}
                    {this.generateTheoryExaminationTable('theoryExaminations', 'theoryExaminations')}
                </>
            );
        }

        if (this.state.licenceType === 'RePL') {
            return (
                <>
                    {/* RPA Rating tables */}
                    {this.generateTitle(
                        'RPA category and type',
                        'rpaRatings',
                        ['rpaCategory', 'rpaType']
                    )}
                    {this.generateTable('rpaRatings', 'rpaCategory')}
                    {this.generateTable('rpaRatings', 'rpaType')}
                </>
            );
        }

        if (this.state.licenceType === 'AROC') {
            return (
                <>
                    {/* Certificate details */}
                    <h4>Certificate details</h4>
                    {this.generateArocCertificateDetailsTable('arocDetails')}
                    <h4>English language proficiency</h4>
                    {this.generateArocElpDetailsTable('arocDetails')}
                </>
            );
        }

        if (this.state.licenceType === 'RPA Excluded Accreditation' || this.state.licenceType === 'RPA Recreational Accreditation') {

            //Create table header for individual
            const tableHeader = {
                title: 'Operator accreditation',
                sortBy: 'category',
                properties: [
                    {
                        "label": "Category",
                        "property": "category",
                        "visible": true,
                        "type": "string",
                        "width": "25%"
                    },
                    {
                        "label": "Issue date",
                        "property": "validFrom",
                        "visible": true,
                        "type": "date",
                        "width": "25%"
                    },
                    {
                        "label": "Expiry date",
                        "property": "validUntil",
                        "visible": true,
                        "type": "date",
                        "width": "25%"
                    },
                    {
                        "label": "Status",
                        "property": "componentStatus",
                        "visible": true,
                        "type": "date",
                        "width": "25%"
                    }
                ]
            };

            const tableBody = [
                {
                    'category': this.props.verificationViewData?.data['Component']['category'],
                    'validFrom': this.props.verificationViewData?.data['Component']['validFrom'] == null ? '' : this.props.verificationViewData?.data['Component']['validFrom'] == null ? '' : moment(this.props.verificationViewData?.data['Component']['validFrom'], 'YYYY-MM-DD').format('DD/MM/YYYY'),
                    'validUntil': this.props.verificationViewData?.data['Component']['validUntil'] == null ? '' : this.props.verificationViewData?.data['Component']['validUntil'] == null ? '' : moment(this.props.verificationViewData?.data['Component']['validUntil'], 'YYYY-MM-DD').format('DD/MM/YYYY'),
                    'componentStatus': this.props.verificationViewData?.data['Component']['componentStatus']
                }
            ];

            return <SortedTable
                caption={tableHeader.title}
                data={tableBody}
                cols={tableHeader.properties}
                sortby={tableHeader.sortBy} />;
        }
    }

    generatePageContent = () => {

        let portalReturnUrl = '';
        if (this.props.formProperties.orgid) {
            portalReturnUrl = '/forms/organisation' + this.props.formProperties.orgid + '/dashorg';
        }
        else {
            portalReturnUrl = '/forms/dashind';
        }

        const breadcrumbs = [
            { text: 'myCASA', link: portalReturnUrl },
            { text: 'Confirm authorisation', link: '/forms/digitallicence/verification' },
            { text: 'Authorisation details' }
        ];

        if (this.state.licenceCancelled) {
            return (
                <Heading
                    text=''
                    breadcrumbs={breadcrumbs}
                    showback={false} />
            );
        }

        if (!this.state.licenceType || !this.state.token) {
            return (
                <>
                    <Heading
                        text=''
                        breadcrumbs={breadcrumbs}
                        showback={true} />

                </>
            );
        }

        return (
            <>
                <Heading
                    text={this.state.licenceType === 'FCL' ? 'Flight crew licence' : this.state.licenceType === 'RePL' ? 'Remote pilot licence (RePL)' : this.state.licenceType === 'AROC' ? 'Aeronautical Radio Operator Certificate' : 'RPA Accreditation'}
                    breadcrumbs={breadcrumbs}
                    showback={true} />               

                <div className='verificationViewTables'>
                    {/* Component tables */}
                    {this.generateIndividualTable('individual')}
                    {this.renderTables()}
                </div>
            </>
        );
    }

    render() {
        if (this.state.formLoaded) {
            let portalReturnUrl = '';
            if (this.props.formProperties.orgid) {
                portalReturnUrl = '/dashorg?casaorgid=' + this.props.formProperties.orgid;
            }
            else {
                portalReturnUrl = '/dashind';
            }
            return (
                <Fragment>

                    {this.generatePageContent()}

                    <div className="au-form-group">
                        <a className="au-direction-link au-direction-link--left" href={portalReturnUrl}>Return to myCASA</a>
                    </div>

                </Fragment>
            );
        }
        return <CASASpinner />;
    }
}

VerificationPage.propTypes = {
    dispatch: PropTypes.func,
    B2CSettings: PropTypes.object,
    envdata: PropTypes.object,
    previousPageLink: PropTypes.string,
    formProperties: PropTypes.object,
    verificationViewData: PropTypes.object,
    match: PropTypes.object
};

const mapStateToProps = (state) => {
    return {
        B2CSettings: state.B2CSettings,
        envdata: state.envdata,
        formProperties: getFormPropertiesSelector(state.formStore),
        verificationViewData: getFormServiceStatusSelector(state.formStore, 'verificationViewData')
    };
};

export default withRouter(connect(mapStateToProps)(VerificationPage));